import { forwardRef, useCallback } from "react";
import {
  Link,
  type LinkProps,
  type NavigateFunction,
  type NavigateOptions,
  NavLink,
  type NavLinkProps,
  type To,
  useNavigate,
} from "react-router-dom";
import {
  mergeRouterViewTransitionOptions,
  useRouterViewTransitionsConfig,
} from "./viewTransitions";

export type RouterLinkProps = LinkProps;
export type RouterNavLinkProps = NavLinkProps;

export const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>(
  function RouterLink({ viewTransition, ...props }, ref) {
    const { enabled: viewTransitionsEnabled } =
      useRouterViewTransitionsConfig();

    return (
      <Link
        ref={ref}
        viewTransition={viewTransition ?? viewTransitionsEnabled}
        {...props}
      />
    );
  },
);

export const RouterNavLink = forwardRef<HTMLAnchorElement, RouterNavLinkProps>(
  function RouterNavLink({ viewTransition, ...props }, ref) {
    const { enabled: viewTransitionsEnabled } =
      useRouterViewTransitionsConfig();

    return (
      <NavLink
        ref={ref}
        viewTransition={viewTransition ?? viewTransitionsEnabled}
        {...props}
      />
    );
  },
);

export function useRouterNavigate(): NavigateFunction {
  const navigate = useNavigate();
  const { enabled: viewTransitionsEnabled } = useRouterViewTransitionsConfig();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        return navigate(to);
      }

      return navigate(
        to,
        mergeRouterViewTransitionOptions(options, viewTransitionsEnabled),
      );
    },
    [navigate, viewTransitionsEnabled],
  );
}
